import { useMemo } from "react";

import { AlertBanner, Button, Modal } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useCampaignEditContext } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/CampaignEditContext";
import { BrandsSetting } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/settings/BrandsSetting";
import { CampaignNameSetting } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/settings/CampaignNameSetting";
import { EmailUpdatesSetting } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/settings/EmailUpdatesSetting";
import { CompetitorsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/competitor-section/CompetitorsSection";
import { getCompetitorDomainErrorKeys } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/competitor-section/utils";
import { DomainsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/DomainsSection";
import { getDuplicateTrackedDomainIndices } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    ExplicitLLModel,
    IRawCompetitor,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

import { DangerZoneSection } from "./settings/danger-zone-section/DangerZoneSection";
import { ModelsSetting } from "./settings/ModelsSetting";

export const CampaignSettingsModal = () => {
    const {
        state: {
            campaignName,
            selectedBrands,
            currentDomains,
            currentCompetitors,
            currentModels,
            isWeeklyReportSubscribed,
            includeSubdomains,
            isReadOnly,
        },
        campaignData,
        dispatch,
        hasChanges,
        isLoading,
        onApprove,
        onClose,
    } = useCampaignEditContext();

    const isSaveEnabled = useMemo<boolean>(() => {
        if (!campaignData || isReadOnly) {
            return false;
        }

        const competitorDomainErrors = getCompetitorDomainErrorKeys(currentCompetitors, currentDomains);
        const dupTrackedDomains = getDuplicateTrackedDomainIndices(currentDomains);

        const isStateValid =
            !!campaignName?.trim() &&
            !!selectedBrands?.length &&
            !competitorDomainErrors.size &&
            !dupTrackedDomains.size;
        return isStateValid && hasChanges;
    }, [
        campaignData,
        hasChanges,
        campaignName,
        selectedBrands?.length,
        isReadOnly,
        currentCompetitors,
        currentDomains,
    ]);

    const setCampaignName = (name: string) => dispatch({ type: "SET_CAMPAIGN_NAME", payload: name });
    const setSelectedBrands = (brands: string[]) => dispatch({ type: "SET_BRANDS", payload: brands });
    const setSelectedModels = (models: ExplicitLLModel[]) => dispatch({ type: "SET_MODELS", payload: models });
    const setOwnerId = (id: number) => dispatch({ type: "SET_OWNER_ID", payload: id });
    const setIsWeeklyReportSubscribed = (value: boolean) => dispatch({ type: "SET_WEEKLY_REPORT", payload: value });
    const setSubdomainsIncluded = (value: boolean) => dispatch({ type: "SET_INCLUDE_SUBDOMAINS", payload: value });
    const setDomains = (domains: IWebsiteItem[]) => dispatch({ type: "SET_DOMAINS", payload: domains });
    const handleCompetitorChange = (index: number, competitor: IRawCompetitor): void => {
        dispatch({
            type: "SET_COMPETITORS",
            payload: (prevCompetitors) => {
                const updatedCompetitors = [...prevCompetitors];
                updatedCompetitors[index] = competitor;
                return updatedCompetitors;
            },
        });
    };

    const handleCompetitorDelete = (index: number): void => {
        dispatch({
            type: "SET_COMPETITORS",
            payload: (prevCompetitors) => prevCompetitors.toSpliced(index, 1),
        });
    };

    return (
        <Modal
            isOpen
            onRequestClose={onClose}
            shouldCloseOnEsc
            shouldCloseOnOverlayClick
            customStyles={{
                content: {
                    width: "550px",
                },
                wrapper: {
                    gap: "16px",
                },
            }}
        >
            <Modal.Header>
                <Flex
                    flexDirection="column"
                    width="100%"
                    gap="24px"
                >
                    <Modal.Title>
                        {translate(
                            isReadOnly
                                ? "llmTracker.campaignQuery.editCampaign.title.read-only"
                                : "llmTracker.campaignQuery.editCampaign.title",
                        )}
                    </Modal.Title>
                    {isReadOnly ? (
                        <AlertBanner variant="info">
                            <AlertBanner.Image />
                            <AlertBanner.Description>
                                {translate("llmTracker.campaignQuery.editCampaign.read-only-banner")}
                            </AlertBanner.Description>
                        </AlertBanner>
                    ) : null}
                </Flex>
            </Modal.Header>
            <Modal.Content>
                <Flex
                    flexDirection="column"
                    padding="8px 0 0 0"
                    gap="24px"
                >
                    <CampaignNameSetting
                        newCampaignName={campaignName}
                        setNewCampaignName={setCampaignName}
                        isReadOnly={isReadOnly}
                    />
                    <DomainsSection
                        title={translate("llmTracker.campaignQuery.edit.domains.title")}
                        subtitle={translate("llmTracker.campaignQuery.edit.domains.subtitle")}
                        domains={currentDomains}
                        changeDomains={setDomains}
                        subdomainsProps={{
                            enabled: true,
                            state: includeSubdomains,
                            onChange: setSubdomainsIncluded,
                            showTooltip: true,
                        }}
                        isReadOnly={isReadOnly}
                    />
                    <BrandsSetting
                        selectedBrands={selectedBrands}
                        setSelectedBrands={setSelectedBrands}
                        isReadOnly={isReadOnly}
                    />
                    <ModelsSetting
                        selectedModels={currentModels}
                        setSelectedModels={setSelectedModels}
                        countryCode={campaignData?.countryCode ?? 0}
                        isLoading={isLoading}
                        isReadOnly={isReadOnly}
                    />
                    <EmailUpdatesSetting
                        isSubscribed={isWeeklyReportSubscribed}
                        onToggle={setIsWeeklyReportSubscribed}
                        isReadOnly={isReadOnly}
                    />
                    <CompetitorsSection
                        title={translate(
                            isReadOnly
                                ? "llmTracker.campaignQuery.edit.competitors.title.read-only"
                                : "llmTracker.campaignQuery.edit.competitors.title",
                        )}
                        competitors={currentCompetitors}
                        trackedDomains={currentDomains}
                        onCompetitorChange={handleCompetitorChange}
                        onCompetitorDelete={handleCompetitorDelete}
                        isReadOnly={isReadOnly}
                    />
                    {!isReadOnly ? <DangerZoneSection onOwnerChange={setOwnerId} /> : null}
                </Flex>
            </Modal.Content>
            <Modal.Footer withBorder>
                <Button
                    type="flat"
                    onClick={onClose}
                    isDisabled={isLoading}
                >
                    {translate(isReadOnly ? "global.close" : "global.cancel")}
                </Button>
                {!isReadOnly ? (
                    <Button
                        type="primary"
                        onClick={onApprove}
                        isLoading={isLoading}
                        isDisabled={!isSaveEnabled}
                    >
                        {translate("global.save")}
                    </Button>
                ) : null}
            </Modal.Footer>
        </Modal>
    );
};
